import { Box, Typography } from '@mui/material';
import type { HostedReport } from '../types/report.types';
import {
  ACCENT_PILL_BG,
  AGENCY_BADGE_BORDER,
  AGENCY_BADGE_FG,
  AGENCY_BAR_BG,
  AGENCY_BAR_FG,
  AGENCY_BUTTON_BORDER,
  FONT,
  HEADING_FG,
  LOGO_TILE_DARK,
  LOGO_TILE_LIGHT,
  MUTED_FG,
  ON_DARK_FG,
  SURFACE,
} from '../reportTokens';

/**
 * The agency's logo, or nothing when it has none.
 *
 * `size` lets the header (44px) and the footer (26px, see `ReportFooter`)
 * share one implementation instead of two near-identical avatar boxes.
 */
export function BrandMark({
  report,
  size = 44,
}: {
  report: HostedReport;
  size?: number;
}) {
  const branding = report.branding;
  if (branding?.logoUrl) {
    // On a tile: the agency bar is painted the brand colour, and a logo drawn
    // in that colour all but disappears on it. White suits nearly every logo,
    // since logo files are made for white; a white-on-transparent one gets a
    // dark tile instead.
    const pad = size >= 40 ? 6 : 4;
    return (
      <Box
        sx={{
          display: 'inline-flex',
          bgcolor:
            branding.logoTone === 'light' ? LOGO_TILE_DARK : LOGO_TILE_LIGHT,
          // Browsers leave backgrounds out of print unless told otherwise, and
          // a white logo's dark tile is what makes it visible on paper.
          printColorAdjust: 'exact',
          WebkitPrintColorAdjust: 'exact',
          borderRadius: size >= 40 ? '10px' : '7px',
          p: `${pad}px ${pad * 1.5}px`,
          lineHeight: 0,
        }}
      >
        <Box
          component='img'
          src={branding.logoUrl}
          alt={branding.brandName || 'Agency logo'}
          sx={{
            maxHeight: size - pad * 2,
            maxWidth: size * 4,
            objectFit: 'contain',
          }}
        />
      </Box>
    );
  }
  return null;
}

/** "Sep 6 at 11:03 AM" — enough to say when this went out without the open/recipient counts the payload does not carry. */
const formatSentAt = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

interface AgencyBarProps {
  report: HostedReport;
  agencyView: boolean;
  onAgencyViewChange: (value: boolean) => void;
  accent: string;
}

/**
 * The report's top chrome: the agency's own mark, the Client/Agency toggle —
 * offered only when the payload says the reader may switch, which the public
 * route never does — and, once switched to Agency view, the action row for
 * this delivery.
 *
 * The mark prints: a downloaded PDF travels without its email, and the intro's
 * "Presented by" line is text only — without the bar, the agency's logo
 * appeared only on the last page. The controls do not print; each carries its
 * own `report-no-print`.
 */
function AgencyBar({
  report,
  agencyView,
  onAgencyViewChange,
  accent,
}: AgencyBarProps) {
  const showAgencyRow = report.canViewAgency && agencyView;

  const handleCopyLink = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(window.location.href).catch(() => {
      // The browser can deny clipboard access; there is nothing useful to do
      // beyond leaving the link exactly as it was.
    });
  };

  // Reuses the page's own `?print=1` handling (see `ReportPage`) rather than
  // rendering a second copy of the document — a new tab so the reader's place
  // in the current one is not lost under the print dialog.
  const handleDownloadPdf = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('print', '1');
    window.open(url.toString(), '_blank', 'noopener');
  };

  return (
    <Box
      data-testid='agency-bar'
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}
        >
          <BrandMark report={report} />
          {report.branding?.brandName && (
            <Typography
              sx={{ fontSize: FONT.body, fontWeight: 700, color: HEADING_FG }}
            >
              {report.branding.brandName}
            </Typography>
          )}
        </Box>
        {report.canViewAgency && (
          <Box
            role='radiogroup'
            aria-label='Report view'
            className='report-no-print'
            sx={{
              display: 'flex',
              bgcolor: SURFACE,
              borderRadius: '9999px',
              p: 0.4,
            }}
          >
            {[
              { label: 'Client view', value: false },
              { label: 'Agency view', value: true },
            ].map(option => (
              <Box
                key={option.label}
                component='button'
                type='button'
                role='radio'
                aria-checked={agencyView === option.value}
                onClick={() => onAgencyViewChange(option.value)}
                sx={{
                  border: 0,
                  cursor: 'pointer',
                  borderRadius: '9999px',
                  px: 1.5,
                  py: 0.6,
                  fontSize: FONT.eyebrow,
                  fontWeight: 700,
                  bgcolor:
                    agencyView === option.value
                      ? ACCENT_PILL_BG
                      : 'transparent',
                  color: agencyView === option.value ? accent : MUTED_FG,
                }}
              >
                {option.label}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {showAgencyRow && (
        <Box
          data-testid='agency-action-row'
          className='report-no-print'
          sx={{
            bgcolor: AGENCY_BAR_BG,
            color: AGENCY_BAR_FG,
            borderRadius: '12px',
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              flexWrap: 'wrap',
            }}
          >
            <Box
              component='span'
              sx={{
                fontSize: FONT.micro,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: AGENCY_BADGE_FG,
                border: '1px solid',
                borderColor: AGENCY_BADGE_BORDER,
                px: 1.1,
                py: 0.3,
                borderRadius: '9999px',
              }}
            >
              Agency
            </Box>
            <Typography sx={{ fontSize: FONT.fine, color: AGENCY_BAR_FG }}>
              {report.sentAt
                ? `Sent ${formatSentAt(report.sentAt)}`
                : 'Not yet sent'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Box
              component='button'
              type='button'
              onClick={handleCopyLink}
              sx={{
                height: 34,
                px: 1.75,
                borderRadius: '9999px',
                border: '1px solid',
                borderColor: AGENCY_BUTTON_BORDER,
                bgcolor: 'transparent',
                color: ON_DARK_FG,
                fontSize: FONT.eyebrow,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Copy share link
            </Box>
            <Box
              component='button'
              type='button'
              onClick={handleDownloadPdf}
              sx={{
                height: 34,
                px: 1.75,
                borderRadius: '9999px',
                border: '1px solid',
                borderColor: AGENCY_BUTTON_BORDER,
                bgcolor: 'transparent',
                color: ON_DARK_FG,
                fontSize: FONT.eyebrow,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Download PDF
            </Box>
            {/* Resend goes out from the agency's authenticated dashboard, not
                from a client's emailed link — this public page has no route
                to reach it. Rendered disabled and muted, with a title
                explaining where it does live, rather than as a live-looking
                control that quietly does nothing when clicked. */}
            <Box
              component='button'
              type='button'
              disabled
              title='Resend is available from the Reports tab in the main app.'
              sx={{
                height: 34,
                px: 2,
                borderRadius: '9999px',
                border: '1px solid',
                borderColor: AGENCY_BUTTON_BORDER,
                bgcolor: 'transparent',
                color: AGENCY_BAR_FG,
                fontSize: FONT.eyebrow,
                fontWeight: 600,
                cursor: 'not-allowed',
                opacity: 0.6,
              }}
            >
              Resend report
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default AgencyBar;
